<template>
  <!-- 填充 -->
  <AttrImage
    class="mt-2.5"
    label="填充"
    :base64="pointer0"
    @change="(image: string) => onImageUpdate(image,'pointer0')"
    @delete="() => onImageDelete('pointer0')"
  />

 

  <!-- 绑定数据 -->
  <div class="mt-2.5">
    <attr-data-source :shape="shape" />
  </div>

  <!-- 指针特效 -->
  <AttrGroup
    title="指针特性"
    :show-add="!showPointerRegion"
    :visible="showPointerRegion"
    card
    @add="() => addOrDelPointerRegion(true)"
  >
    <div>
      <!-- title -->
      <div
        class="h-13 px-5 flex items-center justify-between opacity-70 w-full"
      >
        <span class="text-base">指针特性</span>
        <AttrDeleteIcon
          :class="{
            'attr-disabled': attrDisabled
          }"
          @delete="() => addOrDelPointerRegion(false)"
        />
      </div>
      <!-- 指针光源 -->
      <LightSource
        v-if="canUse('pointer-light-source').valid"
        :shape="shape"
        @on-image-update="onImageUpdate"
        @delete="onImageDelete"
      />
      <!-- 指针阴影 -->
      <AttrImage
        label="指针阴影"
        :base64="pointerShadow"
        @change="(image: string) => onImageUpdate(image,'pointerShadow')"
        @delete="() => onImageDelete('pointerShadow')"
      />
      <AttrInputNumber
        :value="shape.attrs['shadowPointerOffsetY'] ?? 0"
        label="指针阴影Y轴偏移量"
        :precision="0"
        :disabled="!!attrDisabled"
        @change="(val:any)=> shape.setAttrs({
          'shadowPointerOffsetY':val
        })"
      />

      <!-- 秒针旋转 -->
      <AttrSelect
        v-if="shape.attrs.source == 'timeSecond'"
        label="秒针旋转"
        :value="secondHandRotationValue"
        :options="secondHandRotationOptions"
        :width="170"
        :disabled="!!attrDisabled"
        @change="(val:any)=> shape.setAttrs({
          pointerFps:val
        })"
      />
    </div>
  </AttrGroup>

  <!-- 图片输出格式 -->
  <div>
    <AttrImgFomat :shape="shape" />
  </div>
</template>

<script lang="ts" setup>
import { withTempFile } from '@/hooks/use-temp-file';
import { Shape } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { mirrorImg } from '@/utils/transform-image';
import { PropType, computed } from 'vue';


const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();

const attrDisabled = computed(() => editorStore.attrDisabled);

const pointer0 = computed(() => {
  const _pointer0 = props.shape.getResources({
    resourceName: 'pointer0',
    colorGroup: editorStore.themeColor?.id ?? '',
    base64: true
  });
  return _pointer0?.[0] ?? '';
});

const pointerShadow = computed(
  () =>
    props.shape.getResources({
      resourceName: 'pointerShadow',
      colorGroup: editorStore.themeColor?.id ?? '',
      base64: true
    })?.[0]
);

const onImageUpdate = async (image: string, resourceName: ResourceKey) => {
  props.shape?.uploadResource(
    {
      resourceName,
      colorGroup: editorStore.themeColor?.id ?? '',
      realpath: image
    },
    resourceName !== 'pointerShadow'
  );
};

const onImageDelete = async (resourceName: ResourceKey) => {
  props.shape?.deleteResource({
    resourceName,
    colorGroup: editorStore.themeColor?.id ?? ''
  });
};

// const onParameterChange = (e: Event) => {
//   const checked = (e.target as HTMLInputElement)?.checked;
//   props.shape.setAttrs({
//     parameter: checked ? 100 : 1000
//   });
// };

// 指针光源和投影
const showPointerRegion = computed(() => {
  // [2, 4].includes(props.shape.attrs.pointerRegionNum ?? 0);
  return !!props.shape.attrs.pointerRegionNum;
});
// 计算秒针旋转选项（针对M66设备特殊处理）
const secondHandRotationOptions = computed(() => {
  const isM66Device = watchfaceStore.deviceType === 'M66';
  const isSecondDataSource = props.shape.attrs.source === 'timeSecond';

  if (isM66Device && isSecondDataSource) {
    // M66设备且数据源为秒时，只显示跳针选项
    return [{ value: 1, label: '跳针' }];
  }

  // 其他情况显示所有选项
  return [
    { value: 1, label: '跳针' },
    { value: 30, label: '持续旋转（30帧）' },
    { value: 60, label: '持续旋转（60帧）' }
  ];
});

// 计算秒针旋转默认值（针对M66设备特殊处理）
const secondHandRotationValue = computed(() => {
  const isM66Device = watchfaceStore.deviceType === 'M66';
  const isSecondDataSource = props.shape.attrs.source === 'timeSecond';

  if (isM66Device && isSecondDataSource) {
    // M66设备且数据源为秒时，默认为跳针
    return 1;
  }

  return props.shape.attrs.pointerFps ?? 1;
});
const addOrDelPointerRegion = async (status: boolean) => {
  if (attrDisabled.value) return;
  const shape = props.shape;
  const deviceLightSource = canUse('pointer-light-source').valid;
  shape.setAttrs({
    pointerRegionNum: status ? 2 : 0
  });
  if (!status) {
    shape.setAttrs({ shadowPointerOffsetY: 0 });
    const resourceKeys: ResourceKey[] = [
      'pointerShadow',
      'pointer1',
      'pointer2',
      'pointer3'
    ];
    resourceKeys.forEach((key) => {
      shape?.deleteResource({
        resourceName: key
      });
    });
    if (deviceLightSource)
      props.shape.setAttrs({
        renderRule: 'alwaysShow'
      });
  } else {
    let base64Image = await mirrorImg(pointer0.value, 'horizontal');
    // 存储到临时文件并上传
    withTempFile(base64Image, (filePath) =>
      props.shape?.uploadResource(
        {
          resourceName: 'pointer1',
          colorGroup: editorStore.themeColor?.id ?? '',
          realpath: filePath
        },
        true
      )
    );
    if (deviceLightSource)
      props.shape.setAttrs({
        renderRule: 'hideWhenOutRange'
      });
  }
};
</script>
